import type { FC, ReactNode } from 'react'
import type { Testimonial } from '@/types'

import Image from 'next/image'
import { ArrowRight, Check, Quote } from 'lucide-react'
import { ButtonLink } from '@/components/generals/button'
import { SectionHeading } from '@/components/generals/section-heading'
import { aboutImages } from '@/constants/content'

interface AboutTeaserProps {
    testimonial: Testimonial | null
}

const points: string[] = [
    'One team from first concept to final pack down',
    'Premium vendor network across India',
    'Trained manpower for crowds of any size'
]

export const AboutTeaser: FC<AboutTeaserProps> = ({ testimonial }): ReactNode => {
    return (
        <section id='about' aria-labelledby='about-title' className='bg-ivory py-24 lg:py-32'>
            <div className='mx-auto grid max-w-7xl items-center gap-14 px-6 lg:grid-cols-2 lg:gap-20'>
                <div className='relative isolate'>
                    <div className='relative aspect-[4/3] overflow-hidden rounded-[2rem] shadow-[0_40px_80px_-40px_rgba(27,18,25,0.55)]'>
                        <Image
                            src={aboutImages.home.src}
                            alt={aboutImages.home.alt}
                            fill
                            sizes='(min-width: 1024px) 50vw, 100vw'
                            className='object-cover'
                        />
                    </div>

                    <div aria-hidden='true' className='absolute -top-5 -left-5 -z-10 hidden size-28 rounded-3xl border-2 border-gold/50 sm:block' />

                    {testimonial && (
                        <figure className='relative mx-4 -mt-16 rounded-3xl bg-night p-6 text-white shadow-2xl sm:absolute sm:right-[-1.5rem] sm:-bottom-10 sm:mx-0 sm:mt-0 sm:max-w-xs'>
                            <Quote className='size-6 text-gold' aria-hidden='true' />

                            <blockquote className='mt-3 font-heading text-xl leading-snug'>
                                <p data-mdlfy-cms={`testimonials:${testimonial.id}:quote`}>{testimonial.quote}</p>
                            </blockquote>

                            <figcaption className='mt-4 text-sm text-white/60'>
                                <span data-mdlfy-cms={`testimonials:${testimonial.id}:name`}>{testimonial.name}</span>
                            </figcaption>
                        </figure>
                    )}
                </div>

                <div>
                    <SectionHeading
                        eyebrow='About Guju Events'
                        titleId='about-title'
                        title={(
                            <>
                                Crafting experiences that
                                {' '}
                                <em className='text-crimson'>last forever</em>
                            </>
                        )}
                        description='Guju Events is a premium event management company delivering unforgettable weddings, corporate events, garba nights and large-scale exhibitions across India.'
                    />

                    <p className='mt-5 text-base leading-relaxed text-muted-foreground sm:text-lg'>
                        With expertise in handling high-budget productions and large audiences, we combine creativity, precision and execution excellence to deliver flawless results, whether it is a family haldi or a stadium on match day.
                    </p>

                    <ul className='mt-8 space-y-3'>
                        {points.map((point) => (
                            <li key={point} className='flex items-start gap-3 text-ink'>
                                <span className='mt-0.5 inline-flex size-6 shrink-0 items-center justify-center rounded-full bg-crimson/10 text-crimson'>
                                    <Check className='size-3.5' aria-hidden='true' />
                                </span>
                                {point}
                            </li>
                        ))}
                    </ul>

                    <ButtonLink href='/about' variant='outline-dark' size='lg' className='mt-10'>
                        Discover our story
                        <ArrowRight className='size-4' aria-hidden='true' />
                    </ButtonLink>
                </div>
            </div>
        </section>
    )
}
